import type { DropdownState, FocusedElement, UnderlyingSymbolPrices } from '../state/types';
import { ARIA_LABELS } from '../types';
import { ClearButton } from './ClearButton';
import {
    SymbolPickerMenu,
    SymbolPickerMenuTrigger,
    SymbolPickerMenuContent,
    SymbolPickerMenuItem,
    SymbolPickerMenuSpinner,
} from './SymbolPickerMenu';
import { useVirtualizer } from '@tanstack/react-virtual';
import { formatCurrency } from '@thinkalpha/ui-core/numberFormats';
import { cn } from '@thinkalpha/ui-core/utils';
import { useCallback, useEffect, useRef } from 'react';
import { MaskedNumber } from 'src/components/ui/input/masked/MaskedNumber';
import { useEvent } from 'src/hooks/useEvent';

interface OptionsPricePickerProps {
    value: number | undefined;
    onChange: (value: number | string) => void;
    onClear?: () => void;
    clearable?: boolean;
    dropdownOpen: boolean;
    dropdown: DropdownState;
    onDropdownSearch: (term: string) => void;
    onDropdownActiveIndex: (index: number) => void;
    onFocus: (element: FocusedElement) => void;
    underlyingSymbolPrices?: UnderlyingSymbolPrices;
}

export const OptionsPricePicker = ({
    value,
    onChange,
    onClear,
    clearable,
    dropdownOpen,
    dropdown,
    onDropdownSearch,
    onDropdownActiveIndex,
    onFocus,
    underlyingSymbolPrices,
}: OptionsPricePickerProps) => {
    // Ref for the scrollable element in the dropdown
    const virtualScrollRef = useRef<HTMLDivElement>(null);
    // Ref for the dialog element
    const dialogRef = useRef<HTMLDivElement>(null);

    // Set up virtualizer
    const virtualizer = useVirtualizer({
        count: dropdown.filteredItems.length,
        getScrollElement: () => virtualScrollRef.current,
        estimateSize: () => 16,
        // Add some overscan for smoother scrolling
        overscan: 5,
    });

    const dropdownActiveIndexRef = useRef(dropdown.activeIndex);
    if (dropdownActiveIndexRef.current !== dropdown.activeIndex) {
        dropdownActiveIndexRef.current = dropdown.activeIndex;
    }
    // Use a callback ref to ensure the virtualizer measures the scroll element correctly
    const virtualScrollRefCallback = useCallback(
        (node: HTMLDivElement | null) => {
            virtualScrollRef.current = node;
            virtualizer.measure();

            // When the dropdown opens, scroll to the active index
            requestAnimationFrame(() => {
                virtualizer.scrollToIndex(dropdownActiveIndexRef.current, {
                    align: 'center',
                });
            });
        },
        [virtualizer, dropdownActiveIndexRef],
    );

    // Sync active index with virtualizer when keyboard navigating
    useEffect(() => {
        if (dropdown.activeIndex >= 0 && dropdown.activeIndex < dropdown.filteredItems.length) {
            virtualizer.scrollToIndex(dropdown.activeIndex, {
                align: 'auto',
            });
        }
    }, [dropdown.activeIndex, dropdown.filteredItems.length, virtualizer]);

    const handleInputClick = useEvent((e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        onFocus('strikePrice');
    });

    const handleFocus = useEvent((e: React.FocusEvent) => {
        e.preventDefault();
        e.stopPropagation();
    });

    // blur is allowed to propagate to the parent container
    // unless the blur event comes from focus shifting to the dropdown
    const handleBlur = useEvent((e: React.FocusEvent) => {
        const next = e.relatedTarget;
        if (next && dialogRef.current?.contains(next)) {
            e.stopPropagation();
        }
    });

    const handleClearPrice = useEvent(() => {
        if (onClear) {
            onClear();
        } else {
            onChange(0);
        }
    });

    const handleValueChange = useEvent((value: string) => {
        onDropdownSearch(value);
    });

    const formatDisplayPrice = (price: number) => {
        // Type guard: ensure price is a valid number before calling toFixed
        if (typeof price !== 'number' || isNaN(price)) {
            return '$0.00'; // Return a safe default for invalid values
        }
        const hasMoreDecimals = price.toString().split('.')[1]?.length > 2;
        return `$${hasMoreDecimals ? price.toFixed(3) : price.toFixed(2)}`;
    };

    // Determine if we should show the prices header
    const shouldShowPricesHeader = underlyingSymbolPrices && underlyingSymbolPrices.lastPrice !== undefined;

    return (
        <SymbolPickerMenu open={dropdownOpen}>
            <SymbolPickerMenuTrigger>
                <div className="relative flex h-5 min-w-fit">
                    <MaskedNumber
                        value={value}
                        onChange={handleValueChange}
                        onClick={handleInputClick}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        className="h-5 w-16 bg-transparent px-0 text-xs outline-none dark:border-transparent"
                        prefix="$"
                        decimalsLimit={3}
                        decimalScale={2}
                        allowNegativeValue={false}
                        placeholder="Strike"
                        dimensions="sm"
                        tabIndex={-1}
                        aria-label={ARIA_LABELS.strikePrice}
                    />
                    {value !== undefined && clearable && <ClearButton onClick={handleClearPrice} />}
                </div>
            </SymbolPickerMenuTrigger>
            <SymbolPickerMenuContent className="w-24 p-0" ref={dialogRef}>
                {dropdownOpen && (
                    <>
                        {dropdown.state === 'loading' ? (
                            <div className="h-40 overflow-auto rounded-lg">
                                <SymbolPickerMenuSpinner />
                            </div>
                        ) : dropdown.filteredItems.length > 0 ? (
                            <>
                                {shouldShowPricesHeader && (
                                    <div className="sticky top-0 z-10 flex flex-col border-b border-gray-700 bg-gray-800 px-2 py-1 text-xs text-gray-400">
                                        <div>Last Price</div>
                                        <span className="text-sm font-bold text-gray-50">
                                            {underlyingSymbolPrices.lastPrice === undefined
                                                ? '-'
                                                : formatCurrency(underlyingSymbolPrices.lastPrice)}
                                        </span>
                                    </div>
                                )}
                                <div
                                    ref={virtualScrollRefCallback}
                                    className={cn(
                                        'overflow-auto rounded-b',
                                        dropdown.filteredItems.length > 10 ? 'h-40' : 'h-auto',
                                    )}
                                >
                                    <div
                                        style={{
                                            height: `${virtualizer.getTotalSize()}px`,
                                            width: '100%',
                                            position: 'relative',
                                        }}
                                    >
                                        {virtualizer.getVirtualItems().map((virtualItem) => {
                                            const price = dropdown.filteredItems[virtualItem.index];

                                            return (
                                                <div
                                                    key={virtualItem.key}
                                                    style={{
                                                        position: 'absolute',
                                                        top: 0,
                                                        left: 0,
                                                        width: '100%',
                                                        height: `${virtualItem.size}px`,
                                                        transform: `translateY(${virtualItem.start}px)`,
                                                    }}
                                                >
                                                    <SymbolPickerMenuItem
                                                        selected={dropdown.activeIndex === virtualItem.index}
                                                        onMouseEnter={() => onDropdownActiveIndex(virtualItem.index)}
                                                        onSelect={() => onChange(price)}
                                                    >
                                                        <div className="w-20 flex-none">
                                                            {formatDisplayPrice(price)}
                                                        </div>
                                                    </SymbolPickerMenuItem>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </>
                        ) : null}
                    </>
                )}
            </SymbolPickerMenuContent>
        </SymbolPickerMenu>
    );
};
